// components/b2b/PurchaseOrders.jsx
import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';
import { orderService } from '../../services/orderService';
import { DB } from '../../utils/db';

function mapBackendStatus(order) {
  const status = (order.orderStatus || '').toUpperCase();
  if (status === 'CANCELED') return 'canceled';
  if (status === 'BACKORDER') return 'backorder';
  if (status === 'COMPLETED' || status === 'CLOSED') {
    const hasDelivery = (order.items || []).some(item =>
      (item.shipments || []).some(s => s.actualDeliveryDate)
    );
    if (hasDelivery || status === 'CLOSED') return 'delivered';
    return 'shipped';
  }
  if (status === 'OPEN') {
    const hasProcessing = (order.histories || []).some(h =>
      (h.event || '').toLowerCase().includes('process') ||
      (h.message || '').toLowerCase().includes('process') ||
      (h.message || '').toLowerCase().includes('fulfil')
    );
    return hasProcessing ? 'processing' : 'submitted';
  }
  return 'submitted';
}

function mapBackendOrder(order) {
  const d2pStatus = mapBackendStatus(order);
  const items = (order.items || []).map(item => ({
    name: item.name || item.sku,
    sku: item.sku,
    qty: item.qty || 1,
    price: item.price || 0,
    totalPrice: item.totalPrice || (item.price * (item.qty || 1)),
    itemStatus: item.itemStatus,
    shipments: item.shipments || []
  }));

  const timeline = [];
  if (order.startDate) {
    timeline.push({ status: 'submitted', timestamp: order.startDate });
  }
  (order.histories || []).forEach(h => {
    if (h.createdAt) {
      timeline.push({ status: h.status || h.event || 'update', timestamp: h.createdAt, note: h.message });
    }
  });

  const trackingNumbers = [];
  (order.items || []).forEach(item => {
    (item.shipments || []).forEach(s => {
      if (s.trackingNumber) trackingNumbers.push(s.trackingNumber);
    });
  });

  return {
    id: order.id ? `PO-${order.id}` : 'PO-Unknown',
    _backendId: order.id,
    facility: order.businessInfo?.location || order.shipping?.company || 'N/A',
    department: order.businessInfo?.glCode || 'General',
    status: d2pStatus,
    items,
    subtotal: order.totals?.subtotal || 0,
    shipping: order.totals?.shipping || 0,
    total: order.totals?.grand || order.totals?.subtotal || 0,
    createdAt: order.startDate || new Date().toISOString(),
    createdBy: order.email || 'Unknown',
    trackingNumber: trackingNumbers[0] || null,
    timeline: timeline.length > 0 ? timeline : [
      { status: 'submitted', timestamp: order.startDate || new Date().toISOString() }
    ],
    poNumber: order.businessInfo?.poNumber,
    payment: order.payment,
    notes: order.notes || [],
    _raw: order
  };
}

const MOCK_ORDERS = [
  {
    id: 'PO-2025-089',
    facility: 'Regional Medical Center',
    department: 'Surgery',
    status: 'processing',
    items: [
      { name: 'Surgical Suture Kit 4-0 Vicryl', qty: 24, price: 82.00 },
      { name: 'Nitrile Exam Gloves (Box of 100)', qty: 50, price: 12.90 },
      { name: 'Gauze Pads 4x4 (Box of 100)', qty: 30, price: 9.80 }
    ],
    subtotal: 3262.00,
    shipping: 0,
    total: 3262.00,
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    createdBy: 'Jane Smith',
    timeline: [
      { status: 'submitted', timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() },
      { status: 'processing', timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString() }
    ]
  },
  {
    id: 'PO-2025-088',
    facility: 'Outpatient Clinic East',
    department: 'Pharmacy',
    status: 'delivered',
    items: [
      { name: 'Lisinopril 10mg (Bottle of 90)', qty: 100, price: 12.00 },
      { name: 'Metformin 500mg (Bottle of 180)', qty: 75, price: 9.00 }
    ],
    subtotal: 1875.00,
    shipping: 0,
    total: 1875.00,
    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'Michael Chen',
    deliveredAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    timeline: [
      { status: 'submitted', timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString() },
      { status: 'processing', timestamp: new Date(Date.now() - 22 * 60 * 60 * 1000).toISOString() },
      { status: 'shipped', timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString() },
      { status: 'delivered', timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() }
    ]
  },
  {
    id: 'PO-2025-087',
    facility: 'ASC North',
    department: 'General',
    status: 'shipped',
    items: [
      { name: 'N95 Respirator Masks (Box of 20)', qty: 20, price: 22.00 },
      { name: 'Face Shields (Box of 25)', qty: 15, price: 19.50 }
    ],
    subtotal: 732.50,
    shipping: 0,
    total: 732.50,
    createdAt: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
    createdBy: 'Sarah Johnson',
    trackingNumber: 'UPS-1Z999AA10123456784',
    timeline: [
      { status: 'submitted', timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString() },
      { status: 'processing', timestamp: new Date(Date.now() - 46 * 60 * 60 * 1000).toISOString() },
      { status: 'shipped', timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString() }
    ]
  }
];

export function PurchaseOrders({ onNavigate }) {
  const { brand } = useBrand();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usingFallback, setUsingFallback] = useState(false);
  
  useEffect(() => {
    loadOrders();
  }, []);
  
  const loadOrders = async () => {
    setLoading(true);
    try {
      const result = await orderService.getOrders({ pageSize: 50, sortBy: 'startDate', sortAsc: false });
      const hits = result?.hits || [];
      if (hits.length > 0) {
        const apiOrders = hits.map(mapBackendOrder);
        const localOrders = await loadLocalOrders();
        setOrders([...localOrders, ...apiOrders]);
        setUsingFallback(localOrders.length > 0 && hits.length === 0);
      } else {
        const localOrders = await loadLocalOrders();
        setOrders([...localOrders, ...MOCK_ORDERS]);
        setUsingFallback(true);
      }
    } catch (err) {
      console.warn('Failed to load orders from API, using mock/local data:', err.message);
      const localOrders = await loadLocalOrders();
      setOrders([...localOrders, ...MOCK_ORDERS]);
      setUsingFallback(true);
    } finally {
      setLoading(false);
    }
  };

  const loadLocalOrders = async () => {
    const stored = await DB.get('d2p_orders') || [];
    return stored.map(order => ({
      id: order.id || `ORD-${Date.now()}`,
      _backendId: null,
      facility: order.location || order.shippingAddress?.street || 'N/A',
      department: order.glCode || 'General',
      status: order.status || 'submitted',
      items: (order.items || []).map(item => ({
        name: item.name || item.sku,
        sku: item.sku,
        qty: item.qty || 1,
        price: item.price || 0,
        totalPrice: item.total || (item.price * (item.qty || 1))
      })),
      subtotal: order.totals?.subtotal || order.totals?.grand || 0,
      shipping: order.totals?.shipping || 0,
      total: order.totals?.grand || order.totals?.subtotal || 0,
      createdAt: order.createdAt || new Date().toISOString(),
      createdBy: order.email || 'Demo User',
      trackingNumber: null,
      timeline: order.timeline || [
        { status: 'submitted', timestamp: order.createdAt || new Date().toISOString() }
      ],
      poNumber: order.poNumber || null,
      glCode: order.glCode || null,
      location: order.location || null,
      payment: { methodTitle: order.paymentMethod || 'Purchase Order' },
      notes: [],
      _isLocal: true
    }));
  };
  
  const filteredOrders = filter === 'all' 
    ? orders 
    : orders.filter(o => o.status === filter);
  
  const filterOptions = [
    { value: 'all', label: 'All Orders', count: orders.length },
    { value: 'submitted', label: 'Submitted', count: orders.filter(o => o.status === 'submitted').length },
    { value: 'processing', label: 'Processing', count: orders.filter(o => o.status === 'processing').length },
    { value: 'shipped', label: 'Shipped', count: orders.filter(o => o.status === 'shipped').length },
    { value: 'delivered', label: 'Delivered', count: orders.filter(o => o.status === 'delivered').length }
  ];
  
  if (selectedOrder) {
    return <OrderDetail order={selectedOrder} onBack={() => setSelectedOrder(null)} />;
  }
  
  return (
    <div style={{
      background: '#F9FAFB',
      minHeight: '100vh',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
          marginBottom: 32,
          flexWrap: 'wrap',
          gap: 16
        }}>
          <div>
            <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, color: brand.c.navy }}>
              Purchase Orders
            </h1>
            <p style={{ color: '#6B7280' }}>
              Track and manage facility procurement
              {usingFallback && (
                <span style={{ color: '#F59E0B', marginLeft: 8, fontSize: 12 }}>
                  (demo data)
                </span>
              )}
            </p>
          </div>
          <Button icon="plus" onClick={() => onNavigate('catalog')}>
            Create New PO
          </Button>
        </div>
        
        {/* Filter Tabs */}
        <div style={{
          display: 'flex',
          gap: 8,
          marginBottom: 24,
          borderBottom: '2px solid #E5E7EB',
          overflowX: 'auto'
        }}>
          {filterOptions.map(option => (
            <button
              key={option.value}
              onClick={() => setFilter(option.value)}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: 'transparent',
                borderBottom: filter === option.value ? `3px solid ${brand.c.accent}` : '3px solid transparent',
                marginBottom: -2,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 14,
                color: filter === option.value ? brand.c.accent : '#6B7280',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap'
              }}
            >
              {option.label}
              {option.count > 0 && (
                <span style={{
                  marginLeft: 8,
                  padding: '2px 8px',
                  borderRadius: 12,
                  background: filter === option.value ? brand.c.accent : '#E5E7EB',
                  color: filter === option.value ? 'white' : '#6B7280',
                  fontSize: 12,
                  fontWeight: 700
                }}>
                  {option.count}
                </span>
              )}
            </button>
          ))}
        </div>
        
        {/* Loading State */}
        {loading && (
          <Card>
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{
                width: 48, height: 48,
                border: '4px solid #E5E7EB',
                borderTop: `4px solid ${brand.c.accent}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <p style={{ color: '#6B7280' }}>Loading orders...</p>
            </div>
          </Card>
        )}

        {/* Orders List */}
        {!loading && filteredOrders.length === 0 ? (
          <Card>
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: '#F3F4F6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Icon name="clipboard" size={40} color="#9CA3AF" />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>
                No Purchase Orders
              </h3>
              <p style={{ color: '#6B7280', marginBottom: 24 }}>
                {filter === 'all' 
                  ? 'Create your first purchase order from the catalog'
                  : `No orders with ${filter} status`
                }
              </p>
            </div>
          </Card>
        ) : !loading && (
          <div style={{ display: 'grid', gap: 16 }}>
            {filteredOrders.map(order => (
              <Card key={order.id} hover onClick={() => setSelectedOrder(order)}>
                <div style={{ display: 'flex', gap: 20, alignItems: 'start' }}>
                  <div style={{
                    width: 56,
                    height: 56,
                    borderRadius: 12,
                    background: `${brand.c.accent}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Icon name="clipboard" size={28} color={brand.c.accent} />
                  </div>
                  
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      marginBottom: 8,
                      flexWrap: 'wrap'
                    }}>
                      <h3 style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: brand.c.navy,
                        margin: 0
                      }}>
                        {order.id}
                      </h3>
                      <Badge status={order.status} />
                    </div>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: 12,
                      fontSize: 14,
                      color: '#6B7280',
                      marginBottom: 8
                    }}>
                      <div>
                        <strong style={{ color: '#374151' }}>Facility:</strong> {order.facility}
                      </div>
                      <div>
                        <strong style={{ color: '#374151' }}>Department:</strong> {order.department}
                      </div>
                      <div>
                        <strong style={{ color: '#374151' }}>Items:</strong> {order.items.length}
                      </div>
                      <div>
                        <strong style={{ color: '#374151' }}>Total:</strong> ${order.total.toLocaleString()}
                      </div>
                    </div>
                    
                    <div style={{ fontSize: 13, color: '#9CA3AF' }}>
                      Created {new Date(order.createdAt).toLocaleString()} by {order.createdBy}
                    </div>
                  </div>
                  
                  <Icon name="arrowRight" size={20} color="#9CA3AF" style={{ flexShrink: 0 }} />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Order Detail View Component
function OrderDetail({ order, onBack }) {
  const { brand } = useBrand();
  
  const statusConfig = {
    submitted: { icon: 'clipboard', color: '#6B7280', label: 'Submitted' },
    processing: { icon: 'settings', color: brand.c.accent, label: 'Processing' },
    shipped: { icon: 'truck', color: '#7C3AED', label: 'Shipped' },
    delivered: { icon: 'checkCircle', color: '#10B981', label: 'Delivered' },
    canceled: { icon: 'x', color: '#EF4444', label: 'Canceled' },
    backorder: { icon: 'clock', color: '#F59E0B', label: 'Backorder' }
  };
  
  const currentStatus = statusConfig[order.status] || statusConfig.submitted;
  
  return (
    <div style={{
      background: '#F9FAFB',
      minHeight: '100vh',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <Button
          variant="ghost"
          icon="arrowLeft"
          onClick={onBack}
          style={{ marginBottom: 24 }}
        >
          Back to Purchase Orders
        </Button>
        
        {/* Header */}
        <Card style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 20, alignItems: 'start', marginBottom: 24 }}>
            <div style={{
              width: 72,
              height: 72,
              borderRadius: 16,
              background: `${currentStatus.color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Icon name={currentStatus.icon} size={36} color={currentStatus.color} />
            </div>
            
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 12,
                flexWrap: 'wrap'
              }}>
                <h1 style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: brand.c.navy,
                  margin: 0
                }}>
                  {order.id}
                </h1>
                <Badge status={order.status} />
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 16,
                fontSize: 14,
                color: '#6B7280'
              }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                    Order Placed
                  </div>
                  <div>{new Date(order.createdAt).toLocaleString()}</div>
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                    Items
                  </div>
                  <div>{(order.items || []).length} item(s)</div>
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                    Created By
                  </div>
                  <div>{order.createdBy}</div>
                </div>
              </div>

              {(order.poNumber || order.department || order.facility) && (
                <div style={{
                  marginTop: 16,
                  padding: 16,
                  background: '#F0FDF4',
                  borderRadius: 8,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 12,
                  fontSize: 14
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#065F46', gridColumn: '1 / -1', marginBottom: 4 }}>
                    Business Information
                  </div>
                  {order.poNumber && (
                    <div>
                      <div style={{ fontWeight: 600, color: '#065F46', marginBottom: 2 }}>PO Number</div>
                      <div style={{ color: '#047857' }}>{order.poNumber}</div>
                    </div>
                  )}
                  {order.department && order.department !== 'General' && (
                    <div>
                      <div style={{ fontWeight: 600, color: '#065F46', marginBottom: 2 }}>GL Code</div>
                      <div style={{ color: '#047857' }}>{order.department}</div>
                    </div>
                  )}
                  {order.facility && order.facility !== 'N/A' && (
                    <div>
                      <div style={{ fontWeight: 600, color: '#065F46', marginBottom: 2 }}>Location / Facility</div>
                      <div style={{ color: '#047857' }}>{order.facility}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {order.trackingNumber && (
            <div style={{
              padding: 16,
              background: '#EFF6FF',
              borderRadius: 8,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1E40AF', marginBottom: 4 }}>
                  Tracking Number
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#3B82F6', fontFamily: 'monospace' }}>
                  {order.trackingNumber}
                </div>
              </div>
              <Button variant="accent" size="sm" icon="truck">
                Track Shipment
              </Button>
            </div>
          )}
        </Card>
        
        {/* Timeline */}
        <Card style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24, color: brand.c.navy }}>
            Order Timeline
          </h2>
          
          <div style={{ position: 'relative' }}>
            {order.timeline.map((event, i) => {
              const isLast = i === order.timeline.length - 1;
              const config = statusConfig[event.status] || statusConfig.submitted;
              
              return (
                <div key={i} style={{ display: 'flex', gap: 16, marginBottom: isLast ? 0 : 32 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: config.color,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      <Icon name={config.icon} size={24} />
                    </div>
                    {!isLast && (
                      <div style={{
                        width: 2,
                        flex: 1,
                        background: '#E5E7EB',
                        marginTop: 8,
                        marginBottom: 8,
                        minHeight: 40
                      }} />
                    )}
                  </div>
                  
                  <div style={{ flex: 1, paddingTop: 12 }}>
                    <div style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: brand.c.navy,
                      marginBottom: 4,
                      textTransform: 'capitalize'
                    }}>
                      {config.label}
                    </div>
                    <div style={{ fontSize: 14, color: '#6B7280' }}>
                      {new Date(event.timestamp).toLocaleString()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
        
        {/* Items */}
        <Card>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
            Order Items
          </h2>
          
          <div style={{ display: 'grid', gap: 12, marginBottom: 24 }}>
            {order.items.map((item, i) => (
              <div
                key={i}
                style={{
                  padding: 16,
                  background: '#F9FAFB',
                  borderRadius: 8,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 16
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4, color: brand.c.navy }}>
                    {item.name}
                  </div>
                  <div style={{ fontSize: 14, color: '#6B7280' }}>
                    Quantity: {item.qty}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: brand.c.accent }}>
                    ${(item.price * item.qty).toFixed(2)}
                  </div>
                  <div style={{ fontSize: 12, color: '#6B7280' }}>
                    ${item.price.toFixed(2)} each
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div style={{
            paddingTop: 20,
            borderTop: '1px solid #E5E7EB'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 8,
              fontSize: 14
            }}>
              <span style={{ color: '#6B7280' }}>Subtotal</span>
              <span style={{ fontWeight: 600 }}>${order.subtotal.toFixed(2)}</span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 12,
              fontSize: 14
            }}>
              <span style={{ color: '#6B7280' }}>Shipping</span>
              <span style={{ fontWeight: 600, color: '#10B981' }}>FREE</span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingTop: 12,
              borderTop: '1px solid #E5E7EB',
              fontSize: 20,
              fontWeight: 700
            }}>
              <span>Total</span>
              <span style={{ color: brand.c.accent }}>
                ${order.total.toFixed(2)}
              </span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
